import { memo, useEffect } from "react"
import {
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material"
import Tooltip from "components/UI/Tooltip"
import { styles } from "./SelectInput.styles"

export const SelectInput = memo(
  ({
    value,
    options = [],
    placeholder,
    startAdornment,
    renderOption,
    onChange,
    disabled = false,
    isLoading = false,
    autoSelectSingle = false,
    sx = {},
    fullWidth = false,
    ...props
  }) => {
    useEffect(() => {
      if (
        autoSelectSingle &&
        !value &&
        options.length === 1 &&
        typeof onChange === "function"
      ) {
        const singleOption = options[0]
        const singleVal =
          typeof singleOption === "string"
            ? singleOption
            : singleOption.id ?? singleOption.value
        onChange(singleVal)
      }
    }, [autoSelectSingle, value, options, onChange])
    return (
      <Select
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        displayEmpty
        size="small"
        fullWidth={fullWidth}
        disabled={disabled || isLoading}
        startAdornment={startAdornment}
        endAdornment={
          isLoading ? (
            <Box sx={styles.loadingIndicator}>
              <CircularProgress size={16} color="inherit" />
            </Box>
          ) : null
        }
        MenuProps={styles.menuProps}
        sx={[styles.select, ...(Array.isArray(sx) ? sx : [sx])]}
        renderValue={(selected) => {
          if (!selected && placeholder) {
            return (
              <Typography variant="body2" sx={styles.placeholder}>
                {placeholder}
              </Typography>
            )
          }
          if (renderOption) {
            return renderOption(selected, options)
          }
          const option = options.find(
            (o) =>
              String(typeof o === "string" ? o : o.id ?? o.value) ===
              String(selected)
          )
          const label = option
            ? typeof option === "string"
              ? option
              : option.label ?? option.name
            : selected
          return (
            <Tooltip
              title={label}
              enterDelay={300}
              arrow
              divStyle={{ display: "inline-block", maxWidth: "100%" }}
            >
              <Typography variant="body2" sx={styles.valueText}>
                {label}
              </Typography>
            </Tooltip>
          )
        }}
        {...props}
      >
        {options.map((option) => {
          const optValue =
            typeof option === "string" ? option : option.id ?? option.value
          const optLabel =
            typeof option === "string" ? option : option.label ?? option.name

          return (
            <MenuItem key={optValue} value={optValue}>
              {renderOption ? (
                renderOption(optValue, options)
              ) : (
                <Typography variant="body2" sx={styles.menuItemText}>
                  {optLabel}
                </Typography>
              )}
            </MenuItem>
          )
        })}
      </Select>
    )
  }
)

export default SelectInput
